import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, App as AntdApp, Button, Flex, Form, Input, Select } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { organizationsUserGroupsCreateMutation } from 'src/api/main'
import { ResizableDrawer } from 'src/components'
import {
  UserGroupMembersField,
  type GroupMemberValue
} from 'src/features/users/components'
import { invalidateWorkspaceUserGroups } from 'src/features/users/hooks'
import { useShowApiErrors } from 'src/utils'

import { useAppRoleOptions } from '../hooks/useAppGroupRoles'

const FORM_ID = 'app-group-create-form'

type Props = {
  open: boolean
  organizationId: number
  applicationId: number
  onClose: () => void
}

type FormValues = {
  name: string
  users?: GroupMemberValue[]
  roles?: number[]
}

export const AppGroupCreateDrawer = ({
  open,
  organizationId,
  applicationId,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()

  const { roleOptions, isLoading: rolesLoading } = useAppRoleOptions(
    organizationId,
    applicationId,
    open
  )

  const { mutateAsync, isPending } = useMutation(
    organizationsUserGroupsCreateMutation()
  )

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({ name: '', users: [], roles: [] })
  }, [open, form])

  const submit = async (values: FormValues) => {
    try {
      await mutateAsync({
        body: {
          name: values.name.trim(),
          organization: organizationId,
          users: (values.users ?? []).map((user) => user.id),
          roles: values.roles ?? []
        }
      })
      await invalidateWorkspaceUserGroups(queryClient, organizationId)
      void message.success(t('workspace.apps.groups.create.success'))
      onClose()
    } catch (error) {
      showApiErrors(error, form)
    }
  }

  return (
    <ResizableDrawer
      open={open}
      onClose={() => {
        if (!isPending) onClose()
      }}
      defaultWidth={520}
      storageKey="app-group-create-drawer-width"
      title={t('workspace.apps.groups.create.title')}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose} disabled={isPending}>
            {t('workspace.apps.groups.modal.cancel')}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            form={FORM_ID}
            loading={isPending}
          >
            {t('workspace.apps.groups.create.submit')}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={14}>
        <Alert
          type="info"
          showIcon
          title={t('workspace.apps.groups.create.accessNotice')}
        />

        <Form<FormValues>
          id={FORM_ID}
          form={form}
          layout="vertical"
          onFinish={submit}
        >
          <Form.Item
            name="name"
            label={t('users.groups.form.name')}
            rules={[
              { required: true, message: t('users.groups.form.nameRequired') }
            ]}
          >
            <Input placeholder={t('users.groups.form.namePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="users"
            extra={t('workspace.apps.groups.create.membersHint')}
          >
            <UserGroupMembersField workspaceId={organizationId} />
          </Form.Item>

          <Form.Item
            name="roles"
            label={t('workspace.apps.groups.modal.rolesLabel')}
            extra={t('workspace.apps.groups.create.rolesHint')}
          >
            <Select
              mode="multiple"
              allowClear
              loading={rolesLoading}
              options={roleOptions}
              optionFilterProp="label"
              placeholder={t('workspace.apps.groups.modal.rolesPlaceholder')}
            />
          </Form.Item>
        </Form>
      </Flex>
    </ResizableDrawer>
  )
}
